import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import 'iconify-icon';
import { toast } from 'sonner';
import FileUploader from '../../components/admin/FileUploader';

const API_URL = process.env.REACT_APP_BACKEND_URL + '/api';

const authHeaders = () => {
  const token = localStorage.getItem('access_token');
  return token ? { Authorization: `Bearer ${token}` } : {};
};

const CATEGORIES = [
  { value: 'all', label: 'Todos', icon: 'solar:gallery-linear' },
  { value: 'image', label: 'Imagens', icon: 'solar:gallery-wide-linear' },
  { value: 'video', label: 'Vídeos', icon: 'solar:videocamera-linear' },
  { value: 'document', label: 'Documentos', icon: 'solar:document-linear' },
];

const MediaManager = () => {
  const [media, setMedia] = useState([]);
  const [loading, setLoading] = useState(true);
  const [category, setCategory] = useState('all');

  const loadMedia = useCallback(async () => {
    setLoading(true);
    try {
      const url = `${API_URL}/admin/media${category !== 'all' ? `?category=${category}` : ''}`;
      const response = await axios.get(url, { withCredentials: true, headers: authHeaders() });
      setMedia(response.data);
    } catch (err) {
      console.error('Failed to load media:', err);
      toast.error('Erro ao carregar mídia');
    } finally {
      setLoading(false);
    }
  }, [category]);

  useEffect(() => {
    loadMedia();
  }, [loadMedia]);

  const copyUrl = (url) => {
    const absolute = url.startsWith('http') ? url : `${process.env.REACT_APP_BACKEND_URL}${url}`;
    navigator.clipboard.writeText(absolute);
    toast.success('URL copiada!');
  };

  const handleDelete = async (mediaId) => {
    if (!window.confirm('Excluir este arquivo permanentemente?')) return;
    try {
      await axios.delete(`${API_URL}/admin/media/${mediaId}`, {
        withCredentials: true,
        headers: authHeaders(),
      });
      toast.success('Arquivo removido');
      setMedia((curr) => curr.filter((m) => m.media_id !== mediaId));
    } catch (err) {
      console.error('Failed to delete media:', err);
      toast.error('Falha ao remover');
    }
  };

  return (
    <div>
      <div className="flex items-center justify-between mb-6">
        <div>
          <h2 className="text-3xl font-bold mb-2">Biblioteca de Mídia</h2>
          <p className="text-neutral-600">Imagens, vídeos e documentos do site</p>
        </div>
      </div>

      {/* Upload zone (auto refresh after success) */}
      <div className="mb-8 max-w-2xl">
        <FileUploader
          value=""
          onChange={() => loadMedia()}
          accept="all"
          folder="library"
          label="Arraste imagens, vídeos ou documentos aqui — ou clique"
          testId="library-uploader"
        />
      </div>

      {/* Category filter */}
      <div className="flex flex-wrap gap-2 mb-6">
        {CATEGORIES.map((c) => (
          <button
            key={c.value}
            onClick={() => setCategory(c.value)}
            data-testid={`media-filter-${c.value}`}
            className={`flex items-center gap-2 px-4 py-2 rounded-full text-sm transition-colors ${
              category === c.value
                ? 'bg-accent-orange text-white'
                : 'bg-neutral-100 text-neutral-700 hover:bg-neutral-200'
            }`}
          >
            <iconify-icon icon={c.icon}></iconify-icon>
            {c.label}
          </button>
        ))}
      </div>

      {loading ? (
        <div className="flex items-center justify-center h-40">
          <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-accent-orange"></div>
        </div>
      ) : media.length === 0 ? (
        <div className="text-center py-12 text-neutral-500 bg-white rounded-xl border border-neutral-200">
          <iconify-icon icon="solar:gallery-linear" className="text-6xl mb-4"></iconify-icon>
          <p>Nenhum arquivo enviado ainda</p>
        </div>
      ) : (
        <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-4">
          {media.map((item) => {
            const absolute = item.url.startsWith('http')
              ? item.url
              : `${process.env.REACT_APP_BACKEND_URL}${item.url}`;
            return (
              <div
                key={item.media_id}
                className="group relative bg-white rounded-lg border border-neutral-200 overflow-hidden hover:shadow-lg transition-shadow"
                data-testid={`media-card-${item.media_id}`}
              >
                <div className="aspect-square bg-neutral-100 flex items-center justify-center overflow-hidden">
                  {item.category === 'image' ? (
                    <img
                      src={absolute}
                      alt={item.original_name}
                      className="w-full h-full object-cover"
                    />
                  ) : item.category === 'video' ? (
                    <iconify-icon icon="solar:videocamera-linear" className="text-5xl text-zinc-700"></iconify-icon>
                  ) : (
                    <iconify-icon icon="solar:document-linear" className="text-5xl text-zinc-700"></iconify-icon>
                  )}
                </div>
                <div className="absolute top-2 right-2 flex gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                  <button
                    type="button"
                    onClick={() => copyUrl(item.url)}
                    className="w-8 h-8 bg-white/95 hover:bg-white rounded-md flex items-center justify-center shadow"
                    title="Copiar URL"
                  >
                    <iconify-icon icon="solar:copy-linear"></iconify-icon>
                  </button>
                  <button
                    type="button"
                    onClick={() => handleDelete(item.media_id)}
                    className="w-8 h-8 bg-white/95 hover:bg-red-100 rounded-md flex items-center justify-center shadow text-red-600"
                    title="Excluir"
                  >
                    <iconify-icon icon="solar:trash-bin-linear"></iconify-icon>
                  </button>
                </div>
                <div className="p-2">
                  <p className="text-xs text-neutral-700 truncate" title={item.original_name}>
                    {item.original_name}
                  </p>
                  <p className="text-[10px] text-zinc-700">
                    {(item.size / 1024).toFixed(0)} KB · {item.category}
                  </p>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default MediaManager;
